import { useState, useEffect } from 'react'
import { formatearPeso } from '../../utils/formato'
import ModalSeleccionarCliente from '../fiado/ModalSeleccionarCliente'
import { useConfig } from '../../context/ConfigContext'

const METODOS = [
  {
    id: 'efectivo',
    label: 'Efectivo',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="3" />
        <path d="M6 12h.01M18 12h.01" />
      </svg>
    ),
  },
  {
    id: 'tarjeta',
    label: 'Tarjeta',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
        <path d="M6 15h4" />
      </svg>
    ),
  },
  {
    id: 'transferencia',
    label: 'Transfer.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8 6l4-4 4 4M16 18l-4 4-4-4" />
        <path d="M12 2v20" />
      </svg>
    ),
  },
  {
    id: 'fiado',
    label: 'Fiado',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
]

const TECLAS = [['7','8','9'],['4','5','6'],['1','2','3'],['.','0','⌫']]

export default function ModalPago({ total, onConfirmar, onCancelar }) {
  const { config } = useConfig()
  const metodosFiltrados = config.fiadoHabilitado ? METODOS : METODOS.filter(m => m.id !== 'fiado')

  const [metodo, setMetodo] = useState('efectivo')
  const [recibido, setRecibido] = useState('')
  const [clienteFiado, setClienteFiado] = useState(null)
  const [procesando, setProcesando] = useState(false)

  // Resetear estado al cambiar de método
  useEffect(() => { setRecibido(''); setClienteFiado(null) }, [metodo])

  const recibidoNum = parseFloat(recibido) || 0
  const cambio = recibidoNum - total
  const alcanza = recibidoNum >= total

  const puedeConfirmar =
    (metodo === 'efectivo' && alcanza) ||
    (metodo === 'tarjeta') ||
    (metodo === 'transferencia') ||
    (metodo === 'fiado' && clienteFiado != null)

  function presionar(tecla) {
    if (tecla === '⌫') { setRecibido(v => v.slice(0, -1)); return }
    if (tecla === '.' && recibido.includes('.')) return
    if (tecla === '.' && recibido === '') { setRecibido('0.'); return }
    const partes = recibido.split('.')
    if (partes.length === 2 && partes[1].length >= 2) return
    if (partes[0].length >= 6 && tecla !== '.') return
    setRecibido(v => v + tecla)
  }

  const montosRapidos = generarMontosRapidos(total)

  async function confirmar() {
    if (!puedeConfirmar || procesando) return
    setProcesando(true)
    try {
      await onConfirmar({
        metodoPago: metodo,
        recibido: metodo === 'efectivo' ? recibidoNum : null,
        cambio: metodo === 'efectivo' ? cambio : null,
        clienteId: metodo === 'fiado' ? clienteFiado._id : null,
        clienteNombre: metodo === 'fiado' ? clienteFiado.nombre : null,
      })
    } finally {
      setProcesando(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-tienda-surface w-full max-w-lg rounded-t-3xl overflow-hidden flex flex-col max-h-[92vh]">

        {/* Cabecera */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-tienda-text">Forma de pago</h2>
            <p className="text-tienda-muted text-sm mt-0.5">Total a cobrar</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-tienda-accent">{formatearPeso(total)}</p>
          </div>
        </div>

        {/* Selector de método */}
        <div className="flex gap-2 px-5 pb-4 flex-shrink-0">
          {metodosFiltrados.map(m => (
            <button
              key={m.id}
              onClick={() => setMetodo(m.id)}
              className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-2xl border transition-colors
                ${metodo === m.id
                  ? 'bg-tienda-accent/10 border-tienda-accent text-tienda-accent'
                  : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
            >
              {m.icon}
              <span className="text-xs font-semibold">{m.label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* ── EFECTIVO ── */}
          {metodo === 'efectivo' && (
            <div className="px-5 pb-4 flex flex-col gap-4">
              <div className="bg-tienda-bg rounded-2xl border border-tienda-border p-4">
                <p className="text-tienda-muted text-sm mb-1">Importe recibido</p>
                <p className={`text-4xl font-bold tracking-tight min-h-[2.5rem]
                  ${recibido ? (alcanza ? 'text-tienda-success' : 'text-tienda-danger') : 'text-tienda-border'}`}>
                  {recibido ? `$${recibido}` : '$0.00'}
                </p>
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setRecibido(String(total))}
                  className={`flex-1 min-w-[4rem] py-2.5 rounded-xl text-base font-bold border transition-colors
                    ${Math.abs(recibidoNum - total) < 0.001
                      ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                      : 'bg-tienda-bg border-tienda-border text-tienda-text active:bg-tienda-border'}`}
                >
                  Exacto
                </button>
                {montosRapidos.map(m => (
                  <button
                    key={m}
                    onClick={() => setRecibido(String(m))}
                    className={`flex-1 min-w-[4rem] py-2.5 rounded-xl text-base font-bold border transition-colors
                      ${recibidoNum === m
                        ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                        : 'bg-tienda-bg border-tienda-border text-tienda-text active:bg-tienda-border'}`}
                  >
                    ${m}
                  </button>
                ))}
              </div>

              {recibido !== '' && (
                <div className={`rounded-2xl p-4 flex items-center justify-between
                  ${alcanza ? 'bg-tienda-success/10 border border-tienda-success/30' : 'bg-tienda-danger/10 border border-tienda-danger/30'}`}>
                  <span className={`text-base font-semibold ${alcanza ? 'text-tienda-success' : 'text-tienda-danger'}`}>
                    {alcanza ? 'Cambio a devolver' : 'Monto insuficiente'}
                  </span>
                  <span className={`text-2xl font-bold ${alcanza ? 'text-tienda-success' : 'text-tienda-danger'}`}>
                    {alcanza ? formatearPeso(cambio) : `-${formatearPeso(Math.abs(cambio))}`}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                {TECLAS.flat().map(tecla => (
                  <button
                    key={tecla}
                    onPointerDown={e => { e.preventDefault(); presionar(tecla) }}
                    className={`h-14 rounded-2xl text-xl font-bold flex items-center justify-center
                      active:scale-95 transition-transform duration-75 select-none
                      ${tecla === '⌫'
                        ? 'bg-tienda-danger/20 text-tienda-danger border border-tienda-danger/30'
                        : 'bg-tienda-bg border border-tienda-border text-tienda-text active:bg-tienda-border'}`}
                  >
                    {tecla}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── TARJETA ── */}
          {metodo === 'tarjeta' && (
            <InfoMetodo
              emoji="💳"
              titulo="Pago con tarjeta"
              descripcion="Procesa el cobro en tu terminal bancaria y confirma cuando el pago sea aceptado."
              total={total}
            />
          )}

          {/* ── TRANSFERENCIA ── */}
          {metodo === 'transferencia' && (
            <InfoMetodo
              emoji="📲"
              titulo="Transferencia / CoDi"
              descripcion="Solicita la transferencia al cliente y confirma cuando la recibas en tu cuenta."
              total={total}
            />
          )}

          {/* ── FIADO ── */}
          {metodo === 'fiado' && (
            <div className="px-5 pb-4 flex flex-col gap-4">
              <div className="bg-tienda-danger/10 border border-tienda-danger/30 rounded-2xl px-4 py-3 flex items-center gap-3">
                <span className="text-xl">⚠️</span>
                <div>
                  <p className="text-tienda-danger font-semibold text-sm">Venta a crédito</p>
                  <p className="text-tienda-muted text-xs">
                    {formatearPeso(total)} se añadirá a la deuda del cliente
                  </p>
                </div>
              </div>

              {clienteFiado ? (
                <div className="bg-tienda-accent/10 border border-tienda-accent rounded-2xl px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-tienda-accent/20 flex items-center justify-center">
                      <span className="text-tienda-accent font-bold text-lg">
                        {clienteFiado.nombre.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-tienda-text font-semibold">{clienteFiado.nombre}</p>
                      {clienteFiado.telefono && (
                        <p className="text-tienda-muted text-xs">{clienteFiado.telefono}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setClienteFiado(null)}
                    className="text-tienda-muted text-sm underline"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <ModalSeleccionarCliente
                  clienteSeleccionado={clienteFiado}
                  onSeleccionar={setClienteFiado}
                />
              )}
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 p-5 border-t border-tienda-border flex-shrink-0">
          <button onClick={onCancelar} className="btn-ghost flex-1 text-center">
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={!puedeConfirmar || procesando}
            className={`flex-1 text-center text-xl font-bold rounded-2xl px-6 py-4 transition-all
              ${puedeConfirmar && !procesando
                ? 'btn-primary'
                : 'bg-tienda-surface border border-tienda-border text-tienda-muted cursor-not-allowed'}`}
          >
            {procesando ? 'Procesando...' : 'Confirmar cobro'}
          </button>
        </div>
      </div>
    </div>
  )
}

function InfoMetodo({ emoji, titulo, descripcion, total }) {
  return (
    <div className="px-5 pb-4">
      <div className="bg-tienda-bg rounded-2xl border border-tienda-border p-6 flex flex-col items-center text-center gap-3">
        <span className="text-5xl">{emoji}</span>
        <h3 className="text-lg font-bold text-tienda-text">{titulo}</h3>
        <p className="text-tienda-muted text-sm leading-relaxed">{descripcion}</p>
        <div className="w-full border-t border-tienda-border pt-3 mt-1">
          <p className="text-tienda-muted text-sm">Total a cobrar</p>
          <p className="text-3xl font-bold text-tienda-accent mt-1">{formatearPeso(total)}</p>
        </div>
      </div>
    </div>
  )
}

function generarMontosRapidos(total) {
  const candidatos = []
  const billetes = [10, 20, 50, 100, 200, 500, 1000]
  for (const b of billetes) {
    const multiplo = Math.ceil(total / b) * b
    if (multiplo >= total && !candidatos.includes(multiplo)) {
      candidatos.push(multiplo)
    }
    if (candidatos.length >= 4) break
  }
  return candidatos.slice(0, 4)
}
